import type BaseEnumType from "@/app/services/baseEnumType";
import Key from "@/app/types/common/key";
import { translate, translateWithParams } from "@/plugins/i18n";

class NounMessages {
  private _translationKey: string;
  constructor(translationKey: string) {
    this._translationKey = translationKey;
  }

  Singular() {
    return translate(Key.fromString(this._translationKey));
  }

  Plural() {
    return translate(Key.fromString(this._translationKey + "_plural"));
  }

  SingleOrPlural(count: number) {
    return count === 1 ? this.Singular() : this.Plural();
  }

  CopySingle() {
    return translateWithParams("noun_copy_noun", { noun: this.Singular() });
  }

  ViewSingle() {
    return translateWithParams("noun_view_noun", { noun: this.Singular() });
  }

  DeleteSingle() {
    return translateWithParams("noun_delete_noun", { noun: this.Singular() });
  }

  CreateSingle() {
    return translateWithParams("noun_create_noun", { noun: this.Singular() });
  }

  HideSingle() {
    return translateWithParams("noun_hide_noun", { noun: this.Singular() });
  }

  UnhideSingle() {
    return translateWithParams("noun_unhide_noun", { noun: this.Singular() });
  }

  EditSingle() {
    return translateWithParams("noun_edit_noun", { noun: this.Singular() });
  }

  OpenSingle() {
    return translateWithParams("noun_open_noun", { noun: this.Singular() });
  }

  AddSingle() {
    return translateWithParams("noun_add_noun", { noun: this.Singular() });
  }
}

export class EnumNoun implements BaseEnumType {
  private static allValues: EnumNoun[] = []; // make sure this is top-most

  public static batch = new EnumNoun(1, "Batch", "enum_noun_batch");
  public static recipe = new EnumNoun(2, "Recipe", "enum_noun_recipe");
  public static process = new EnumNoun(3, "Process", "enum_noun_process");
  public static outletSubType = new EnumNoun(
    4,
    "OutletSubType",
    "enum_noun_outlet_sub_type"
  );
  public static order = new EnumNoun(5, "Order", "enum_noun_order");
  public static outlet = new EnumNoun(6, "Outlet", "enum_noun_outlet");
  public static group = new EnumNoun(7, "Group", "enum_noun_group");
  public static dutyReturn = new EnumNoun(
    8,
    "DutyReturn",
    "enum_noun_duty_return"
  );
  public static credit = new EnumNoun(9, "Credit", "enum_noun_credit");
  public static pricingCategory = new EnumNoun(
    10,
    "PricingCategory",
    "enum_noun_pricing_category"
  );
  public static product = new EnumNoun(11, "Product", "enum_noun_product");
  public static discount = new EnumNoun(12, "Discount", "enum_noun_discount");
  public static pallet = new EnumNoun(13, "Pallet", "enum_noun_pallet");
  public static item = new EnumNoun(14, "Item", "enum_noun_item");
  public static ullage = new EnumNoun(15, "Ullage", "enum_noun_ullage");

  public static stockTransfer = new EnumNoun(
    16,
    "StockTransfer",
    "enum_noun_stock_transfer"
  );

  public static stockBatch = new EnumNoun(
    17,
    "StockBatch",
    "enum_noun_stock_batch"
  );

  public static contact = new EnumNoun(18, "Contact", "enum_noun_contact");
  public static regionalTax = new EnumNoun(
    18,
    "RegionalTax",
    "enum_noun_regional_tax"
  );
  public static purchaseOrder = new EnumNoun(
    19,
    "PurchaseOrder",
    "enum_noun_purchase_order"
  );

  public key: number;
  public value: string;
  public translationKey: string;

  private constructor(key: number, value: string, translationKey: string) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    EnumNoun.allValues.push(this);
  }

  public static getValues(): EnumNoun[] {
    return EnumNoun.allValues;
  }

  public static getByKey(key: number): EnumNoun {
    const found = EnumNoun.allValues.find((e) => e.key === key);
    if (found === undefined) {
      throw new Error(`EnumNoun - Key not found: ${key}`);
    }

    return found;
  }

  get messages(): NounMessages {
    return new NounMessages(this.translationKey);
  }
}
